import { FormEvent, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Lock, ShieldCheck, Loader2 } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ResetPasswordProps {
  resetPasswordToken: string
  updatePasswordUrl: string
  minimumPasswordLength: number
  locale: string
  logoUrl: string
  translations: {
    resetPassword: string
    resetPasswordDesc: string
    newPassword: string
    confirmPassword: string
    changePassword: string
    minLength: string
  }
}

interface SharedProps {
  flash: { alert?: string; notice?: string }
  errors: Record<string, string[]>
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ResetPassword({
  resetPasswordToken,
  updatePasswordUrl,
  minimumPasswordLength,
  locale,
  logoUrl,
  translations: t,
}: ResetPasswordProps) {
  const { errors } = usePage<SharedProps>().props

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [processing, setProcessing] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.put(
      updatePasswordUrl,
      {
        user: {
          reset_password_token: resetPasswordToken,
          password,
          password_confirmation: passwordConfirmation,
        },
      },
      { onFinish: () => setProcessing(false) },
    )
  }

  const allErrors: string[] = []
  if (errors) {
    Object.entries(errors).forEach(([field, msgs]) => {
      msgs.forEach((msg) => allErrors.push(`${field} ${msg}`))
    })
  }

  return (
    <>
      <Head title={t.resetPassword} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              <ShieldCheck className="h-7 w-7 text-[#2DD4BF]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.resetPassword}</h1>
            <p className="text-sm text-slate-400">{t.resetPasswordDesc}</p>
          </div>

          {allErrors.length > 0 && (
            <Alert variant="destructive" className="mb-5 rounded-xl border-red-200 bg-red-50/80">
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {allErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-500">
                {t.newPassword}
              </Label>
              {minimumPasswordLength > 0 && (
                <p className="text-xs text-slate-400">
                  {t.minLength.replace('%{count}', String(minimumPasswordLength))}
                </p>
              )}
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                  autoComplete="new-password"
                  autoFocus
                  required
                  minLength={minimumPasswordLength}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password_confirmation" className="text-xs font-medium text-slate-500">
                {t.confirmPassword}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  id="password_confirmation"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={processing}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t.changePassword
              )}
            </Button>
          </form>
        </div>
      </AuthLayout>
    </>
  )
}
